import React, {useEffect, useState} from 'react';
import {Dialog} from 'primereact/dialog';
import {AdminButton, AdminInputDropdown} from '../../../layout/admin';
import AdminTextArea from '../../../layout/admin/AdminTextArea';
import {Publication} from '../types/Publication';
import publicationsService from '../services/publicationsService';
import archivesService from '../services/archivesService';
import {handleError} from '../../../common/utils/errorHandling';
import {useTranslation} from "react-i18next";

interface ArchiveImportDialogProps {
  visible: boolean;
  onHide: () => void;
  onImportComplete: () => void;
}

const ArchiveImportDialog: React.FC<ArchiveImportDialogProps> = ({
  visible,
  onHide,
  onImportComplete
}) => {
  const {t} = useTranslation();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [publicationId, setPublicationId] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchPublications();
    }
  }, [visible]);

  const fetchPublications = async (): Promise<void> => {
    await handleError(
      async () => {
        const publicationsData = await publicationsService.getAllPublications();
        setPublications(publicationsData);
      },
      t('admin.archives.errors.fetching')
    );
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    await handleError(
      async () => {
        await archivesService.importArchives(publicationId, csvContent);
        alert(t('admin.archives.import.success'));
        handleDialogHide();
        onImportComplete();
      },
      t('admin.archives.errors.importing'),
      () => alert(t('admin.archives.errors.importing'))
    );

    setLoading(false);
  };

  const handleDialogHide = (): void => {
    setPublicationId('');
    setCsvContent('');
    onHide();
  };

  const publicationOptions = [
    ...publications.map(pub => ({
      label: pub.name,
      value: pub.id
    }))
  ];

  return (
    <Dialog
      header={t('admin.archives.import.title')}
      visible={visible}
      style={{width: '50vw'}}
      onHide={handleDialogHide}
      maskClassName="bg-black bg-opacity-50"
    >
      <form onSubmit={handleSubmit}>
        <AdminInputDropdown
          value={publicationId}
          onChange={(e) => setPublicationId(e.target.value)}
          name="publicationId"
          id="publicationId"
          label={t('admin.archives.fields.publication')}
          options={publicationOptions}
          required
        />

        <AdminTextArea
          value={csvContent}
          onChange={(e) => setCsvContent(e.target.value)}
          name="csvContent"
          id="csvContent"
          label={t('admin.archives.import.csvContent')}
          placeholder={t('admin.archives.import.placeholder')}
          rows={12}
          required
          className="mt-4"
        />

        <div className="text-sm text-gray-600 mt-2 mb-4">
          <strong>{t('admin.archives.import.expectedFormat')}</strong> {t('admin.archives.import.formatDescription')}
          <br />
          <strong>{t('admin.archives.import.example')}</strong>
          <pre className="bg-gray-100 p-2 mt-1 rounded text-xs">
            IHS UJ,Instytut Historii Sztuki Uniwersytetu Jagiellońskiego, Kraków<br/>
            MNK,Muzeum Narodowe w Krakowie
          </pre>
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <AdminButton
            type="button"
            label={t('admin.common.actions.cancel')}
            onClick={handleDialogHide}
            variant="white"
          />
          <AdminButton
            type="submit"
            label={t('admin.common.actions.import')}
            variant="black"
            disabled={loading || !publicationId || !csvContent}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default ArchiveImportDialog;

